/* premium-motion.js -- motion for the THRUX premium home (SPEC §0.4).
 * Vanilla, no dependencies. Everything is gated so content is correct if JS fails,
 * if the animation timeline is throttled (automation/background tabs), or under
 * prefers-reduced-motion. Load with `defer`. Target < 8KB.
 *
 *   reveal-up        : [data-reveal] fades/rises on scroll-into-view; children stagger 80ms.
 *   can-drift        : per-element phase offsets so cans drift out of sync (CSS runs the loop).
 *   pointer-parallax : .p-parallax inside [data-parallax] eases toward the cursor (fine pointer only).
 *
 * SAFETY NET (Danny PDP-blank QC, C 2026-09-08): [data-reveal] starts at opacity:0 and must NEVER
 * stay invisible. Layers: (a) initReveal wrapped in try/catch -> reveal all on throw; (b) a one-time
 * window 'error' listener during load -> reveal all; (c) a 1500ms timer that force-reveals only the
 * [data-reveal] elements currently IN the viewport that the observer has not revealed yet, re-armed
 * once on the first scroll (below-the-fold keeps the normal fade); (d) the run-once guard flag is set
 * only AFTER initReveal is bound, so a synchronous throw before that lets a later copy of the script retry.
 */
(function () {
  'use strict';
  // Run-once guard: sections may each include this script; execute a single time
  // so reveal observers / parallax listeners are never double-bound. The flag is SET at the end
  // (constraint d) so a throw during synchronous setup lets a later copy retry.
  if (window.__thruxPremiumMotion) return;

  var doc = document, root = doc.documentElement;
  root.classList.add('js');

  var reduce = false;
  try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  // ---- safety net helper: reveal every not-yet-revealed [data-reveal] (content correct if JS fails) ----
  function revealAll() {
    try {
      var all = doc.querySelectorAll('.thrux-premium [data-reveal]:not([data-revealed])');
      for (var i = 0; i < all.length; i++) all[i].setAttribute('data-revealed', '');
    } catch (e) {}
  }
  // (b) any uncaught page error during load reveals everything, so a broken script elsewhere on the
  //     page can never leave a premium section stuck at opacity:0.
  try { window.addEventListener('error', revealAll, { once: true }); } catch (e) {}

  // ---- throttle detector (build pattern #21): if the timeline isn't ticking,
  //      snap reveals visible so they never get stuck at opacity:0. ----
  try {
    var t1 = doc.timeline && doc.timeline.currentTime;
    setTimeout(function () {
      var t2 = doc.timeline && doc.timeline.currentTime;
      if (t2 === t1 || typeof t2 !== 'number') root.classList.add('thrux-no-anim');
    }, 100);
  } catch (e) { root.classList.add('thrux-no-anim'); }

  function onReady(fn) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }

  // (c) force-reveal any [data-reveal] currently intersecting the viewport that still lacks
  //     data-revealed (covers "observer never fired": hidden tab that later shows, WebKit quirk, etc.).
  function forceRevealInView() {
    try {
      var vh = window.innerHeight || doc.documentElement.clientHeight || 0;
      var els = doc.querySelectorAll('.thrux-premium [data-reveal]:not([data-revealed])');
      for (var i = 0; i < els.length; i++) {
        var r = els[i].getBoundingClientRect();
        if (r.top < vh && r.bottom > 0) els[i].setAttribute('data-revealed', '');
      }
    } catch (e) { revealAll(); }
  }

  // ---- reveal-up ----
  function initReveal() {
    try {
      var els = doc.querySelectorAll('.thrux-premium [data-reveal]');
      if (!els.length) return;
      if (reduce || !('IntersectionObserver' in window)) {
        for (var i = 0; i < els.length; i++) els[i].setAttribute('data-revealed', '');
        return;
      }
      var io = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          var el = en.target;
          // stagger direct [data-reveal] children if this is a group; else reveal self
          var kids = el.hasAttribute('data-reveal-group')
            ? el.querySelectorAll(':scope > [data-reveal], :scope [data-reveal-child]') : null;
          if (kids && kids.length) {
            for (var k = 0; k < kids.length; k++) (function (n, idx) {
              setTimeout(function () { n.setAttribute('data-revealed', ''); }, idx * 80);
            })(kids[k], k);
          }
          el.setAttribute('data-revealed', '');
          obs.unobserve(el);
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
      for (var j = 0; j < els.length; j++) io.observe(els[j]);
    } catch (e) {
      // (a) if the reveal wiring throws, snap all content visible rather than leave it blank
      revealAll();
    }
  }

  // ---- can-drift phase offsets (CSS owns the keyframes; JS only varies the phase) ----
  function initDrift() {
    if (reduce) return;
    var cans = doc.querySelectorAll('.thrux-premium .p-can--drift');
    for (var i = 0; i < cans.length; i++) {
      var c = cans[i];
      if (!c.style.getPropertyValue('--drift-dur')) c.style.setProperty('--drift-dur', (6 + (i % 3) * 1.4).toFixed(1) + 's');
      if (!c.style.getPropertyValue('--drift-delay')) c.style.setProperty('--drift-delay', (-(i * 0.9)).toFixed(1) + 's');
    }
  }

  // ---- pointer-parallax (fine pointer only, rAF-throttled) ----
  function initParallax() {
    if (reduce) return;
    var fine = false;
    try { fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches; } catch (e) {}
    if (!fine) return;
    var scopes = doc.querySelectorAll('.thrux-premium [data-parallax]');
    scopes.forEach(function (scope) {
      var layers = scope.querySelectorAll('.p-parallax');
      if (!layers.length) return;
      var tx = 0, ty = 0, cx = 0, cy = 0, raf = 0;
      var max = 12; // px, SPEC cap
      function loop() {
        cx += (tx - cx) * 0.12; cy += (ty - cy) * 0.12;
        for (var i = 0; i < layers.length; i++) {
          var depth = parseFloat(layers[i].getAttribute('data-depth') || '1');
          layers[i].style.setProperty('--px', (cx * depth).toFixed(2) + 'px');
          layers[i].style.setProperty('--py', (cy * depth).toFixed(2) + 'px');
        }
        if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) raf = requestAnimationFrame(loop);
        else raf = 0;
      }
      scope.addEventListener('pointermove', function (e) {
        var r = scope.getBoundingClientRect();
        tx = ((e.clientX - r.left) / r.width - 0.5) * 2 * max;
        ty = ((e.clientY - r.top) / r.height - 0.5) * 2 * max;
        if (!raf) raf = requestAnimationFrame(loop);
      }, { passive: true });
      scope.addEventListener('pointerleave', function () { tx = 0; ty = 0; if (!raf) raf = requestAnimationFrame(loop); }, { passive: true });
    });
  }

  onReady(function () { initReveal(); initDrift(); initParallax(); });

  // (c) belt: after 1.5s force-reveal in-view [data-reveal] the observer hasn't reached, then re-arm
  //     once on the first scroll for elements newly brought into view. Below-the-fold keeps its fade.
  setTimeout(function () {
    forceRevealInView();
    var onScrollOnce = function () { forceRevealInView(); window.removeEventListener('scroll', onScrollOnce); };
    try { window.addEventListener('scroll', onScrollOnce, { passive: true, once: true }); } catch (e) {}
  }, 1500);

  // (d) mark done only now that initReveal + the safety nets are bound; a throw above lets a copy retry.
  window.__thruxPremiumMotion = true;
})();
